import styles from './GlobalSearch.module.scss';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import SearchBarAndIcon from './Components/SearchBarAndIcon/SearchBarAndIcon';
import useSearchResultFetcher, { GlobalSearchResult } from './useSearchResultFetcher';
import { plusPurpleCircle } from '../../assets/hostedassets';
import AddToWatchlistDropdown from '../../modules/Investor/Portfolio/components/AddToWatchlist/AddToWatchlistDropdown';
import { Mixpanel } from '../../utils/mixpanel';
import { useAppSelector } from '../../app/hooks';
import { BULK_ADD } from '../../utils/constants/mixpanelEvents/investorEvents';
import BulkAddModal from '../BulkAdd/BulkAddModal';

type GlobalSearchProps = {
    showSearch: boolean;
    setShowSearch: Dispatch<SetStateAction<boolean>>;
};

const GlobalSearch = (props: GlobalSearchProps) => {
    const { showSearch, setShowSearch } = props;
    const [isBulkOpen, setIsBulkOpen] = useState(false);
    const userName = useAppSelector((state) => state.user.userData?.userName);
    const userEmail = useAppSelector((state) => state.user.userData?.primaryEmail);
    const pnoId = useAppSelector((state) => state.user.userData?.investorOrgId);
    const pnoName = useAppSelector((state) => state.user.userData?.pnoName);

    const {
        setSearchText,
        searchText,
        searchResults,
        navigateOrAddLead,
        setSearchResults,
        fetchCompanyInfoForResult,
        handleAddNewFlow,
        searchLoading,
    } = useSearchResultFetcher();

    const handleSelectAlreadyAdded = async (result: GlobalSearchResult) => {
        const accessType = result.companyAccessType || '';
        const resultWithGroup = { ...result, companyAccessType: accessType };
        setShowSearch(false);
        navigateOrAddLead(result.orgId, accessType, resultWithGroup, searchText);
    };

    const handleSelectAddNew = async (result: GlobalSearchResult) => {
        const accessType = result.companyAccessType || '';
        const resultWithGroup = { ...result, companyAccessType: accessType };
        setShowSearch(false);
        await handleAddNewFlow({
            baseResult: resultWithGroup,
            accessType,
            searchTextValue: searchText,
            initialPrefillLoadingFields: { website: true, brand: true },
            overwriteIdentifierFromCompanyInfo: true,
        });
    };
    const trackBulkEvent = () => {
        Mixpanel.track(BULK_ADD, {
            User_Name: userName,
            User_Email: userEmail,
            PNO_ID: pnoId,
            PNO_Name: pnoName,
        });
    };

    const handleFooterAddNew = async () => {
        setShowSearch(false);
        const syntheticResult = {
            orgId: null,
            orgName: searchText,
            websiteUrl: '',
        };
        await handleAddNewFlow({
            baseResult: syntheticResult,
            accessType: '',
            searchTextValue: searchText,
            initialPrefillLoadingFields: { identifier: true, website: true, brand: true },
            overwriteIdentifierFromCompanyInfo: true,
            isAddUsingSearchText: true,
        });
    };
    useEffect(() => {
        if (!showSearch) {
            setSearchText('');
            setSearchResults(null);
        }
    }, [showSearch]);

    return (
        <div style={{ position: 'relative' }}>
            {showSearch ? <div className={styles.SearchBackdrop} onClick={() => setShowSearch(false)} /> : null}
            <div
                className={`${styles.GlobalSearchWrapper} ${
                    showSearch ? styles.GlobalSearchWrapperExpanded : styles.GlobalSearchWrapperCompact
                }`}
            >
                <SearchBarAndIcon
                    setSearchText={setSearchText}
                    searchText={searchText}
                    showSearch={showSearch}
                    onCrossIconClick={() => setShowSearch(false)}
                    onSearchIconClick={() => setShowSearch(!showSearch)}
                    onBulkAddClick={() => {
                        trackBulkEvent();
                        setShowSearch(false);
                        setIsBulkOpen(true);
                    }}
                    searchLoading={searchLoading}
                />
            </div>

            <div style={{ position: 'relative', height: 'auto' }}>
                <AddToWatchlistDropdown
                    visible
                    searchText={searchText}
                    searchResults={searchResults}
                    wrapperClassName={styles.SearchResults}
                    onSelectAlreadyAdded={handleSelectAlreadyAdded}
                    onSelectAddNew={handleSelectAddNew}
                    footerVisible={searchText.length >= 3}
                    footerLabel={`Add using "${searchText}"`}
                    onFooterAddNew={handleFooterAddNew}
                />
                {isBulkOpen && <BulkAddModal initialText="" onClose={() => setIsBulkOpen(false)} />}
            </div>
        </div>
    );
};

export default GlobalSearch;
